import {useCallback, useContext, useEffect} from 'react';
import {channel} from "redux-saga";
import {call, getContext, put, setContext, take} from 'redux-saga/effects';
import {AppContext} from '../app_context';
import CancelTokenSource from "./cancel";

export function* signalReady() {
    const readyCh = yield getContext('readyCh')
    readyCh.close();
}

export function* withFeedbackChannel(channel, saga, ...args) {
    yield setContext({feedback: channel});
    return yield call(saga, ...args);
}

function* putFeedbackSaga(value) {
    const channel = yield getContext('feedback');
    if (channel) {
        yield put(channel, value);
    }
}

export function putFeedback(value) {
    return call(putFeedbackSaga, value);
}

const useSagaRunner = (feedbackCh) => {
    const {sagaMiddleware, readyCh} = useContext(AppContext);
    return useCallback((token, saga, ...args) => {
        const taskCh = channel();
        const task = sagaMiddleware.run(function* () {
            const task = yield take(taskCh);
            yield setContext({readyCh});
            return yield call(withFeedbackChannel, feedbackCh, function* () {
                yield putFeedback({error: null});
                try {
                    return yield call([task, saga], ...args);
                } catch (error) {
                    yield putFeedback({error});
                    throw error;
                }
            });
        });
        token?.register(() => {
            try {
                if (task.isRunning() && !task.isCancelled()) {
                    task.cancel();
                }
            } catch {
                // FIXME: redux-saga throws `currCb.cancel is not a function`,
                // though it is not clear why
            }
        });
        taskCh.put(task);
        taskCh.close();
        return task;
    }, [sagaMiddleware, feedbackCh, readyCh]);
};

export function* bypassingReadyChecks(saga, ...args) {
    yield setContext({ready: true});
    yield call(saga, ...args);
}

export const useSagaEffect = (feedbackCh, saga) => {
    const run = useSagaRunner(feedbackCh);
    useEffect(() => {
        const source = new CancelTokenSource();
        run(source.token, saga).toPromise().catch(() => {
            // silently ignored, use error feedback
        });
        return () => source.cancel();
    }, [run, saga]);
};

export const useSagaCallback = (feedbackCh, saga, cancelToken=null) => {
    const run = useSagaRunner(feedbackCh);
    return useCallback(
        (...args) => run(cancelToken, saga, ...args).toPromise(),
        [run, cancelToken, saga]
    );
};
